import { GridTableRecord } from '@apex/component-library/components/GridTable/types'
import { Box, Link } from '@chakra-ui/react'
import { GridTable, GridTableColumn } from '@apex/component-library'
import { isEmpty } from 'lodash'
import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { RoutesEnum } from '../../types/RoutesEnum'
import { formatDateAndTime } from '../../utils'
import { CurrencyEnum, numberToCurrency } from '../../utils/numberToCurrency'
import { formatTimeString, normalizeTimeString } from '../../utils/timeUtil'
import {
  AcatsSearchDto,
  AcatsTransfer,
  AlpsRecommendedAction,
  AlpsRecommendedActionDisplay,
  AlpsStatus,
  AlpsStatusDisplay,
  NsccStatus,
  NsccStatusDisplay,
  ProcessingDirection,
  ProcessingDirectionDisplay,
  RejectReason,
  RejectReasonDisplay,
  TransferType,
  TransferTypeDisplay,
} from '../../api/types'
import {
  ALPS_RECOMMENDED_ACTION_OPTIONS,
  ALPS_STATUS_OPTIONS,
  NSCC_STATUS_OPTIONS,
  PROCESSING_DIRECTION_OPTIONS,
  REJECT_REASON_OPTIONS,
  TRANSFER_TYPE_OPTIONS,
} from './constants'
import assembleQuery from '../../containers/providers/helpers/assembleQuery'

export interface TransferResultsTableProps {
  transferResults: AcatsTransfer[]
  height: number
  width: number
  acatsSearchDto: AcatsSearchDto
}

const assembleColumn = (
  label: string,
  recordField: string,
  width: number,
  options?: {
    value: string
    label: string
  }[],
  cellRenderer?: ({ value }: { value: any }) => JSX.Element,
  isVisibleByDefault: boolean = true,
  alignment?: GridTableColumn['alignment']
): GridTableColumn => ({
  key: recordField,
  label,
  recordField,
  width,
  isFilterable: true,
  isSortable: true,
  isVisibleByDefault,
  alignment,
  ...(!isEmpty(options) && { options }),
  ...(cellRenderer && { cellRenderer }),
})

const transformTransfers = (
  transferResults: AcatsTransfer[]
): GridTableRecord[] =>
  transferResults.map(
    ({
      acatControlNumber = '',
      createdDate = '',
      cycle = '',
      accountNumber = '',
      customerName = '',
      transferType = '',
      processingDirection = '',
      totalAccountValue = '',
      rejectReason = '',
      nsccStatus = '',
      ageInStatus = '',
      contraAccountNumber = '',
      contraFirmName = '',
      contraClearingNumber = '',
      alpsStatus = '',
      acatKey = '',
      age = '',
      alpsActionReason = '',
      alpsRecommendedAction = '',
      lastAction = '',
      userOverrideAction = '',
    }) => {
      return {
        key: acatControlNumber,
        acatControlNumber,
        createdDate,
        cycle,
        accountNumber,
        customerName,
        transferType,
        processingDirection,
        totalAccountValue,
        rejectReason,
        nsccStatus,
        ageInStatusSortValue: normalizeTimeString(ageInStatus || ''),
        contraAccountNumber,
        contraFirmName,
        contraClearingNumber,
        alpsStatus,
        acatKey,
        age,
        alpsActionReason,
        alpsRecommendedAction,
        lastAction,
        userOverrideAction,
      }
    }
  )

const AgeInStatusRenderer = ({ value }: { value: string }) => (
  <span>{formatTimeString(value)}</span>
)

const CreatedDateRenderer = ({ value }: { value: string }) => (
  <span>{formatDateAndTime(value)}</span>
)

const TransferTypeCell = ({ value }: { value: any }) => (
  <span>{TransferTypeDisplay[(value as unknown) as TransferType]}</span>
)

const ProcessingDirectionCell = ({ value }: { value: any }) => (
  <span>
    {ProcessingDirectionDisplay[(value as unknown) as ProcessingDirection]}
  </span>
)
const RejectReasonCell = ({ value }: { value: any }) => (
  <span>{RejectReasonDisplay[(value as unknown) as RejectReason]}</span>
)
const TotalAccoutValueCell = ({ value }: { value: any }) => {
  return !value || value === 'pending' ? (
    <Box>Pending</Box>
  ) : (
    <Box>{numberToCurrency(value, CurrencyEnum.usd)}</Box>
  )
}
const NsccStatusCell = ({ value }: { value: any }) => (
  <span>{NsccStatusDisplay[(value as unknown) as NsccStatus]}</span>
)
const AlpsStatusCell = ({ value }: { value: any }) => (
  <span>{AlpsStatusDisplay[(value as unknown) as AlpsStatus]}</span>
)
const AlpsRecommendedActionCell = ({ value }: { value: any }) => (
  <span>
    {AlpsRecommendedActionDisplay[(value as unknown) as AlpsRecommendedAction]}
  </span>
)

const TransferResultsTable = ({
  transferResults,
  height,
  width,
  acatsSearchDto,
}: TransferResultsTableProps) => {
  const searchQuery = assembleQuery(acatsSearchDto)

  const ControlNumberRenderer = ({
    value: acatControlNumber,
  }: {
    value: any
  }) => (
    <Link
      as={ReactRouterLink}
      color="apex.link"
      to={`/${RoutesEnum.TransferReview}/${acatControlNumber}${searchQuery}`}
    >
      {acatControlNumber}
    </Link>
  )

  const columns: GridTableColumn[] = [
    assembleColumn(
      'Control Number',
      'acatControlNumber',
      170,
      undefined,
      ControlNumberRenderer
    ),
    assembleColumn(
      'Created Date',
      'createdDate',
      220,
      undefined,
      CreatedDateRenderer
    ),
    assembleColumn('Cycle', 'cycle', 120),
    assembleColumn('Account Number', 'accountNumber', 155),
    assembleColumn('Customer Name', 'customerName', 160),
    assembleColumn(
      'Transfer Type',
      'transferType',
      155,
      TRANSFER_TYPE_OPTIONS,
      TransferTypeCell
    ),
    assembleColumn(
      'Processing Direction',
      'processingDirection',
      170,
      PROCESSING_DIRECTION_OPTIONS,
      ProcessingDirectionCell
    ),
    assembleColumn(
      'Est. Value',
      'totalAccountValue',
      150,
      undefined,
      TotalAccoutValueCell,
      undefined,
      'right'
    ),
    assembleColumn(
      'Reject Reason',
      'rejectReason',
      150,
      REJECT_REASON_OPTIONS,
      RejectReasonCell
    ),
    assembleColumn(
      'NSCC Status',
      'nsccStatus',
      150,
      NSCC_STATUS_OPTIONS,
      NsccStatusCell
    ),
    assembleColumn(
      'Age in Status',
      'ageInStatusSortValue',
      200,
      undefined,
      AgeInStatusRenderer
    ),
    assembleColumn('Contra Account Number', 'contraAccountNumber', 200),
    assembleColumn('Contra Firm Name', 'contraFirmName', 200),
    assembleColumn('Contra Firm #', 'contraClearingNumber', 140),
    assembleColumn(
      'ALPS Status',
      'alpsStatus',
      150,
      ALPS_STATUS_OPTIONS,
      AlpsStatusCell
    ),
    assembleColumn('ACAT Key', 'acatKey', 150, undefined, undefined, false),
    assembleColumn('Age', 'age', 130, undefined, undefined, false),
    assembleColumn(
      'ALPS Action Reason',
      'alpsActionReason',
      180,
      undefined,
      undefined,
      false
    ),
    assembleColumn(
      'ALPS Recommended Action',
      'alpsRecommendedAction',
      230,
      ALPS_RECOMMENDED_ACTION_OPTIONS,
      AlpsRecommendedActionCell,
      false
    ),
    assembleColumn(
      'Last Action',
      'lastAction',
      150,
      ALPS_RECOMMENDED_ACTION_OPTIONS,
      AlpsRecommendedActionCell,
      false
    ),
    assembleColumn(
      'User Override Action',
      'userOverrideAction',
      200,
      undefined,
      undefined,
      false
    ),
  ]

  return (
    <GridTable
      title="Results"
      columns={columns}
      records={transformTransfers(transferResults)}
      height={height}
      width={width}
      exportFileName={`apex-account-transfer-${new Date()
        .toISOString()
        .replace('Z', '')}`}
    />
  )
}

export default TransferResultsTable
